import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ApiService } from 'shared-lib';
import { CONFIG } from '../constants/config';

export interface SendOtpPayload {
  eventCode?: string;
  userPhoneNumber: string;
  deviceId?: string;
}

export interface ValidateOtpPayload {
  eventCode?: string;
  otpToken: string;
  userId?: string;
  otp: string;
}

export interface ResendOtpPayload {
  eventCode?: string;
  userPhoneNumber: string;
  deviceId?: string;
  otpToken?: string;
}

export interface GiftToFnfOtpPayload {
  customerCode: string;
  deviceId: string;
}

@Injectable({
  providedIn: 'root',
})
export class OtpService {
  private readonly apiService = inject(ApiService);

  /**
   * Send OTP to user's phone number
   */
  sendOtp(phoneNumber: string, eventCode?: string, deviceId?: string): Observable<any> {
    if (!phoneNumber) {
      return throwError(() => new Error('Phone number is required'));
    }

    const payload: SendOtpPayload = {
      eventCode: eventCode || CONFIG.EVENT_CODE,
      userPhoneNumber: phoneNumber,
      deviceId: deviceId || CONFIG.DEVICE_ID,
    };

    return this.apiService.post(`v1/superapp/otp/public/send`, payload);
  }

  sendPrivateOtp(payload: any): Observable<any> {
    return this.apiService.post('v1/superapp/otp/user/send', payload);
  }

  resendPrivateOtp(payload: any): Observable<any> {
    return this.apiService.post('v1/superapp/otp/user/resend', payload);
  }

  sendGiftToFnfOtp(payload: GiftToFnfOtpPayload): Observable<any> {
    return this.apiService.post('v1/superapp/gift-to-fnf/send-otp', payload);
  }

  resendGiftToFnfOtp(payload: GiftToFnfOtpPayload): Observable<any> {
    return this.apiService.post('v1/superapp/gift-to-fnf/resend-otp', payload);
  }

  /**
   * Validate OTP entered by user
   */
  validateOtp(otp: string, otpToken: string, userId?: string, eventCode?: string): Observable<any> {
    if (!otp) {
      return throwError(() => new Error('OTP is required'));
    }
    if (!otpToken) {
      return throwError(() => new Error('OTP token is required'));
    }

    const payload: ValidateOtpPayload = {
      eventCode: eventCode || CONFIG.EVENT_CODE,
      otpToken,
      userId,
      otp,
    };

    return this.apiService.post(`v1/superapp/otp/public/validate`, payload);
  }

  validatePrivateOtp(otp: string, otpToken: string, userId?: string, eventCode?: string): Observable<any> {
    return this.apiService.post(`v1/superapp/otp/user/validate`, {
      eventCode: eventCode || CONFIG.EVENT_CODE,
      otpToken,
      userId,
      otp,
    });
  }

  validatePhoneOtp(otpToken: string): Observable<any> {
    return this.apiService.post(`v1/superapp/validate/phone`, {
      otpToken,
    });
  }

  /**
   * Resend OTP to user's phone number
   */
  resendOtp(phoneNumber: string, eventCode?: string, deviceId?: string, otpToken?: string): Observable<any> {
    if (!phoneNumber) {
      return throwError(() => new Error('Phone number is required'));
    }

    const payload: ResendOtpPayload = {
      eventCode: eventCode || CONFIG.EVENT_CODE,
      userPhoneNumber: phoneNumber,
      deviceId: deviceId || CONFIG.DEVICE_ID,
    };

    if (otpToken) {
      payload.otpToken = otpToken;
    }

    return this.apiService.post(`v1/superapp/otp/public/resend`, payload);
  }

  validateLinkedAccount(payload: { otpToken: string; linkedAccountSID: string }): Observable<any> {
    return this.apiService.post(`v1/superapp/customer-link-account/validate-linkable`, payload);
  }
}
